'use client'

import _ from 'lodash'
import Image from 'next/image'
import { memo, useCallback } from 'react'
import { HiOutlineTrash } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'
import { SoldBanner } from '@/components'

type CardHeaderProps = {
  alt?: string | null
  photoCount?: number | null
  photoUrl?: string | null
  className?: string
  isSold?: boolean
  isDeleted?: boolean
}

// Components
const PhotoCountBadge = memo(({ count }: { count: number }) => (
  <div className='absolute top-4 left-0 flex items-center rounded-r-full bg-zinc-700/70 py-1.5 pr-3 pl-2 text-white'>
    <span className='text-sm font-medium'>{count}</span>
    <span className='sr-only'> photos</span>
  </div>
))

const DeletedIcon = memo(() => (
  <div className='absolute bottom-5 left-5 rounded-full bg-orange-800 p-3 ring-1 ring-white dark:ring-transparent' title='Deleted'>
    <span className='sr-only'>Deleted</span>
    <HiOutlineTrash className='size-6 text-white' aria-hidden='true' />
  </div>
))

export const CardPhoto = memo(({ alt, className, photoCount, photoUrl, isSold, isDeleted }: CardHeaderProps) => {
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.onerror = null // Prevent infinite fallback loops
    e.currentTarget.src = '/img/noPhoto.svg'
  }, [])

  return (
    <figure className={twMerge('relative m-0 block border-b bg-zinc-100/70 dark:bg-transparent', className)}>
      {(photoCount || 0) > 0 && <PhotoCountBadge count={photoCount || 0} />}
      {isSold && <SoldBanner />}
      {isDeleted && <DeletedIcon />}
      <Image
        width={480}
        height={480}
        src={photoUrl || '/img/noPhoto.svg'}
        alt={alt || 'Photo'}
        priority={true}
        fetchPriority='high'
        unoptimized={true}
        className={_.isEmpty(photoUrl) ? 'card-img-no' : 'card-img'}
        onError={handleImageError}
      />
    </figure>
  )
})
